import ProductAvailability from "helpers/product-availability";
import Routes from "../routes";
import SizeHelper from "helpers/form-field-size-helper";
import { skuInfo } from "helpers/sku-helpers";
import { LocalStorage } from "site/localStorage";

import type {
  ImagePathPerSize,
  Product as GodmotherServedProductType,
  SKU as GodmotherSkuObjectType,
  MembershipState as MembershipStateType,
  NotifyMeItem,
  NotifyMeItemProductDetails,
  StaticProductSkuObjectType,
} from "@rtr/godmother";
import type { DiscoProduct as DiscoServedProductType } from "components/types/disco-product";
import type { ClientSideProductDetailsType, ClientNotifyMeProductType } from "components/types/notify-me-product";

import { discoArrayToProductImageStruct, selectMainImageGroup } from "helpers/product-image-helpers";
import ProductImagePath from "helpers/product-image-path";

export const NOTIFY_ME_LOCAL_STORAGE_NAMESPACE = "notifyMe";
export const NOTIFY_ME_LOCAL_STORAGE_KEY = "existingNotifyMeProducts";
export const NOTIFY_ME_GTM_LOCAL_STORAGE_KEY = "GTM";

/**
 * Type declaration that handles the logic of the createNotifyMeProduct arguments such that:
 * - If StaticProduct is undefined, LegacyProduct must be defined with type GodmotherProductType
 * - Otherwise, StaticProduct must be defined, and LegacyProduct could be of type GodmotherProductType or undefined
 */
type CreateNotifyMeProductDetailsParams =
  | {
      discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails;
      godmotherProduct: GodmotherServedProductType;
      membershipState?: MembershipStateType;
    }
  | {
      discoProduct: DiscoServedProductType | NotifyMeItemProductDetails;
      godmotherProduct?: GodmotherServedProductType;
      membershipState?: MembershipStateType;
    };

/**
 * Function that takes in disco served and godmother served product data to create a product details needed to operate notify-me-modal components.
 * notifyMeProductDetails should only be used in notifyMe components as a wrapper in extracting two data sources
 * and should NEVER be pushed into redux or mutate it's arguments
 *
 * @param {params} CreateNotifyMeProductDetailsParams
 * @return {ClientSideProductDetailsType}
 */
export function createNotifyMeProductDetails(params: CreateNotifyMeProductDetailsParams): ClientSideProductDetailsType {
  const { discoProduct, godmotherProduct, membershipState } = params;

  function createProductId(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    return discoProduct?.productId ?? (godmotherProduct?.id || "");
  }

  function createDesignerDisplayName(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    return discoProduct?.designerDisplayName ?? (godmotherProduct?.designer?.displayName || "");
  }

  function createDisplayName(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    return discoProduct?.displayName ?? (godmotherProduct?.displayName || "");
  }

  function createFallbackSrc(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    let fallbackSrc = ProductImagePath;
    if (Array.isArray(discoProduct?.images)) {
      const imagesObject: Partial<Record<string, ImagePathPerSize>> = discoArrayToProductImageStruct(
        discoProduct.images
      );
      fallbackSrc += selectMainImageGroup(imagesObject, "legacyPath")?.["1080x"] || "";
    } else if (typeof godmotherProduct?.images === "object") {
      fallbackSrc += selectMainImageGroup(godmotherProduct.images)?.["1080x"] || "";
    }

    return fallbackSrc;
  }

  function createImagesObject(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    if (Array.isArray(discoProduct?.images)) {
      return discoArrayToProductImageStruct(
        discoProduct.images.map((imgUrl: string) => imgUrl.replace("270x", "1080x"))
      );
    } else if (typeof godmotherProduct?.images === "object") {
      return godmotherProduct.images;
    }

    return {};
  }

  function createSkusArray(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType,
    membershipState?: MembershipStateType
  ) {
    const unavailableSizes: string[] = getUnavailableSizes(godmotherProduct, membershipState);

    if (Array.isArray(discoProduct?.skus)) {
      return discoProduct.skus.map((sku: StaticProductSkuObjectType) => ({
        id: sku.sku,
        sizeDisplayText: sku.sizeDisplayText as string,
        isUnavailable: unavailableSizes.includes(skuInfo(sku.sku).size as string),
        size: skuInfo(sku.sku).size,
        styleName: skuInfo(sku.sku).id,
      }));
    } else if (Array.isArray(godmotherProduct?.skus) && !godmotherProduct.skus.find(sku => !sku.id)) {
      return godmotherProduct.skus.map((sku: GodmotherSkuObjectType) => {
        const getDisplayText = (sku: GodmotherSkuObjectType) =>
          godmotherProduct?.sizeScale?.sizeNames[sku.size as string]
            ? godmotherProduct?.sizeScale?.sizeNames[sku.size as string]
            : sku.size;
        const size = sku.size ?? skuInfo(sku.id).size;
        return {
          id: sku.id,
          sizeDisplayText: getDisplayText(sku) as string, // sku.size as a backup display value
          isUnavailable: unavailableSizes.includes(sku.size as string),
          size,
          styleName: sku.styleName ?? skuInfo(sku.id).id,
        };
      });
    } else {
      return [];
    }
  }

  function createPdpUrl(
    discoProduct?: DiscoServedProductType | NotifyMeItemProductDetails,
    godmotherProduct?: GodmotherServedProductType
  ) {
    if (discoProduct?.pdpUrl) {
      return discoProduct?.pdpUrl;
    } else if (godmotherProduct?.id) {
      return Routes.PDP.RouteById(godmotherProduct.id);
    }

    return "";
  }

  const product = {
    designerDisplayName: createDesignerDisplayName(discoProduct, godmotherProduct),
    displayName: createDisplayName(discoProduct, godmotherProduct),
    fallbackSrc: createFallbackSrc(discoProduct, godmotherProduct),
    images: createImagesObject(discoProduct, godmotherProduct),
    pdpUrl: createPdpUrl(discoProduct, godmotherProduct),
    productId: createProductId(discoProduct, godmotherProduct),
    skus: createSkusArray(discoProduct, godmotherProduct, membershipState),
  } as ClientSideProductDetailsType;

  return product;
}

/*
  ClientNotifyMeProductType GETTER FUNCTIONS
*/

export const getAllNotifiedSKUs = (productId: string, existingNotifyMeProducts: ClientNotifyMeProductType[]) => {
  if (!productId || !existingNotifyMeProducts || !Array.isArray(existingNotifyMeProducts)) return [];
  const alreadySubscribedProduct = existingNotifyMeProducts.find(
    subscribedProduct => subscribedProduct.style === productId
  );
  return alreadySubscribedProduct ? alreadySubscribedProduct.skus : [];
};

export const getSizeDisplayTextFromSku = (sku: string, productDetails: ClientSideProductDetailsType) => {
  if (!sku || !productDetails || !Array.isArray(productDetails.skus)) return "";
  return productDetails.skus.find(skuObject => skuObject.id === sku)?.sizeDisplayText;
};

/*
  GodmotherServed GETTER FUNCTIONS
*/

/**
 * @returns array of sizes that have 0 count
 */
export const getUnavailableSizes = (product?: GodmotherServedProductType, membershipState?: MembershipStateType) => {
  const skuMap = ProductAvailability.sizeToAvailabilityCountMap(product, membershipState) || {};
  return Object.keys(skuMap).filter(sku => !skuMap[sku].count);
};

/*
  notifyMe Local Storage helpers
*/

export const updateNotifyMeLocalStorage = (newNotifyMeProducts: NotifyMeItem) => {
  // users coming to manage-notifications page should always update local storage
  // but let's keep the data structure on the notifyMeProducts the same as if we were
  // fetching the array without productDetails
  function formatProductsForLocalStorage(notifyMeProducts: NotifyMeItem) {
    if (!Array.isArray(notifyMeProducts) || notifyMeProducts?.length < 1) return [];

    return Object.values(notifyMeProducts).map((product: object) => {
      return {
        ...product,
        productDetails: void 0, // we don't want to store productDetails in local storage
      };
    });
  }

  const existingNotifyMeProducts = formatProductsForLocalStorage(newNotifyMeProducts);
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  // @ts-expect-error localStorageClient.set not defined in TS
  localStorageClient.set(NOTIFY_ME_LOCAL_STORAGE_KEY, {
    existingNotifyMeProducts,
    timestamp: Date.now(),
  });
};

export const getNotifyMeLocalStorage = () => {
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  // @ts-expect-error localStorageClient.get not defined in TS
  return localStorageClient.get(NOTIFY_ME_LOCAL_STORAGE_KEY);
};

export const clearNotifyMeLocalStorage = () => {
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  // @ts-expect-error localStorageClient.remove not defined in TS
  localStorageClient.remove(NOTIFY_ME_LOCAL_STORAGE_KEY);
};

/*
  notifyMe validators
*/

export const isNotifyMeAvailable = (
  selectedSize: string,
  product: GodmotherServedProductType,
  membershipState: MembershipStateType
) => {
  const productIsEligbleForUser = ProductAvailability.checkProductIsEligibleForUser(product, membershipState);
  const allSizesAreUnavailable =
    SizeHelper.hasProductSizes(product) && ProductAvailability.allSizesUnavailable(product, membershipState);
  const isOneSizeAndOutOfStock =
    SizeHelper.isOneSizeFitsAll(product) && ProductAvailability.isOneSizeProductOutOfStock(product);
  const selectedSizeIsOutOfStock = Boolean(
    selectedSize &&
      !SizeHelper.isOneSizeFitsAll(product) &&
      !ProductAvailability.hasSkuCount(selectedSize, product, membershipState)
  );

  return Boolean(
    productIsEligbleForUser && (allSizesAreUnavailable || isOneSizeAndOutOfStock || selectedSizeIsOutOfStock)
  );
};

// if the productId is in existingNotifyMeProducts, we can assume it is a notify me product
// and we don't need to check for the specific SKU
export const isNotifyMeProduct = (productId: string, existingNotifyMeProducts: ClientNotifyMeProductType[]) => {
  if (!productId || !existingNotifyMeProducts || !Array.isArray(existingNotifyMeProducts)) return false;
  return existingNotifyMeProducts.some(subscribedProduct => subscribedProduct.style === productId);
};

export function areNotifyMeProductDetailsValid(productDetails: Partial<ClientSideProductDetailsType>) {
  if (!productDetails || typeof productDetails !== "object") return false;

  // Validate all string params in productDetails are non-empty strings
  const stringParams = Object.entries(productDetails).filter(([, value]) => typeof value === "string");
  const allStringParamsValid = stringParams.every(([, value]) => value !== null && value !== "");

  return Boolean(
    allStringParamsValid &&
      productDetails?.images &&
      selectMainImageGroup(productDetails.images)?.["1080x"] &&
      Array.isArray(productDetails.skus) &&
      productDetails.skus.length > 0
  );
}

///
///   New PDP specific notify me helpers
///

export const isNotifyMeAvailableForNewPDP = (
  productIsEligbleForUser: boolean,
  allSizesAreUnavailable: boolean,
  selectedSizeUnavailable: boolean,
  isOneSizeProduct: boolean
) => {
  const selectedSizeIsOutOfStock = Boolean(selectedSizeUnavailable && isOneSizeProduct);

  return Boolean(productIsEligbleForUser && (allSizesAreUnavailable || selectedSizeIsOutOfStock));
};
